import { IconType } from "react-icons";
import {
  FiArchive,
  FiCheckCircle,
  FiCheckSquare,
  FiGrid,
  FiInbox,
  FiLayers,
  FiSettings,
  FiUserCheck,
  FiUsers,
} from "react-icons/fi";
import {
  APPLICATION_TYPES,
  ApplicationType,
} from "../../Reducer/ApplicationTypeReducer";

export type SidebarRole = "client" | "staff" | "admin";
export type SidebarPlacement = "top" | "bottom";

export interface SidebarItem {
  key: string;
  label: string;
  path: string;
  Icon: IconType;
  placement: SidebarPlacement;
  /** Rendered greyed out and non-clickable when false. */
  enabled: boolean;
  visibleTo: SidebarRole[];
  applicationTypes: ApplicationType[];
  requiresSelectedClient: boolean;
  requiresApplicationType: boolean;
}

const ALL_TYPES: ApplicationType[] = [...APPLICATION_TYPES];
const STAFF: SidebarRole[] = ["staff", "admin"];

export const sidebarItems: SidebarItem[] = [
  {
    key: "overview",
    label: "Overview",
    path: "/overview",
    Icon: FiGrid,
    placement: "top",
    enabled: true,
    visibleTo: ["client", "staff", "admin"],
    applicationTypes: ALL_TYPES,
    requiresSelectedClient: true,
    requiresApplicationType: false,
  },
  {
    key: "business-clients",
    label: "Business Clients",
    path: "/",
    Icon: FiUsers,
    placement: "top",
    enabled: true,
    visibleTo: STAFF,
    applicationTypes: ALL_TYPES,
    requiresSelectedClient: false,
    requiresApplicationType: false,
  },
  {
    key: "processing",
    label: "Processing",
    path: "/processing",
    Icon: FiLayers,
    placement: "top",
    enabled: true,
    visibleTo: STAFF,
    applicationTypes: ["costs", "supplierStatements"],
    requiresSelectedClient: true,
    requiresApplicationType: true,
  },
  {
    key: "processing-client",
    label: "Processing",
    path: "/",
    Icon: FiLayers,
    placement: "top",
    enabled: true,
    visibleTo: ["client"],
    applicationTypes: ["costs", "supplierStatements"],
    requiresSelectedClient: false,
    requiresApplicationType: true,
  },
  {
    key: "inbox",
    label: "Inbox",
    path: "/inbox",
    Icon: FiInbox,
    placement: "top",
    enabled: true,
    visibleTo: STAFF,
    applicationTypes: ["costs", "supplierStatements"],
    requiresSelectedClient: true,
    requiresApplicationType: true,
  },
  {
    key: "to-review",
    label: "To Review",
    path: "/to-review",
    Icon: FiCheckSquare,
    placement: "top",
    enabled: true,
    visibleTo: STAFF,
    applicationTypes: ["costs"],
    requiresSelectedClient: true,
    requiresApplicationType: true,
  },
  {
    key: "ready",
    label: "Ready",
    path: "/ready",
    Icon: FiCheckCircle,
    placement: "top",
    enabled: true,
    visibleTo: STAFF,
    applicationTypes: ["costs"],
    requiresSelectedClient: true,
    requiresApplicationType: true,
  },
  {
    key: "archive",
    label: "Archive",
    path: "/archive",
    Icon: FiArchive,
    placement: "top",
    enabled: true,
    visibleTo: STAFF,
    applicationTypes: ["costs", "supplierStatements"],
    requiresSelectedClient: true,
    requiresApplicationType: true,
  },
  {
    key: "user-management",
    label: "User Management",
    path: "/user-management",
    Icon: FiUserCheck,
    placement: "bottom",
    enabled: true,
    visibleTo: ["admin"],
    applicationTypes: ALL_TYPES,
    requiresSelectedClient: true,
    requiresApplicationType: true,
  },
  {
    key: "business-settings",
    label: "Business Settings",
    path: "/Business-settings",
    Icon: FiSettings,
    placement: "bottom",
    enabled: true,
    visibleTo: ["admin"],
    applicationTypes: ALL_TYPES,
    requiresSelectedClient: true,
    requiresApplicationType: true,
  },
];

const CLIENT_ROLE_ID = 1;
const STAFF_ROLE_ID = 0;

export const resolveSidebarRole = (roleId?: number | null): SidebarRole => {
  if (roleId === CLIENT_ROLE_ID) return "client";
  if (roleId === STAFF_ROLE_ID) return "staff";
  return "admin";
};

export interface SidebarAccess {
  roleId?: number | null;
  hasSelectedClient: boolean;
  applicationType: ApplicationType | null;
}

const isAccessible = (item: SidebarItem, access: SidebarAccess): boolean => {
  if (!item.visibleTo.includes(resolveSidebarRole(access.roleId))) return false;
  if (item.requiresSelectedClient && !access.hasSelectedClient) return false;

  if (!access.applicationType) return !item.requiresApplicationType;
  return item.applicationTypes.includes(access.applicationType);
};

export const getSidebarItems = (access: SidebarAccess): SidebarItem[] =>
  sidebarItems.filter((item) => isAccessible(item, access));

const normalizePath = (path: string) => {
  const trimmed = path.replace(/\/+$/, "").toLowerCase();
  return trimmed === "" ? "/" : trimmed;
};

/** Sub-routes (e.g. /inbox/duplicate-invoice-compare) follow their parent item. */
export const isPathAllowed = (path: string, access: SidebarAccess): boolean => {
  const target = normalizePath(path);

  return getSidebarItems(access).some((item) => {
    const itemPath = normalizePath(item.path);
    if (itemPath === "/") return target === "/";
    return target === itemPath || target.startsWith(`${itemPath}/`);
  });
};

/** First page a user can open for their role and application type. */
export const getLandingPath = (access: SidebarAccess): string => {
  const landingItem = getSidebarItems(access).find(
    (item) => item.placement === "top" && item.enabled,
  );
  return landingItem?.path ?? "/overview";
};
